import {useEffect, useRef} from 'react';
import {LANDING} from '../../config/landingConfig';
import {COLORS} from '../../config/colors';
import {trackEvent} from '../../utils/analytics';
import SkazkiCard from '../skazki/SkazkiCard';

// Набор линейных иконок (24×24, stroke=currentColor) — цвет задаётся снаружи.
const ICONS = {
    moon: <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8z"/>,
    heart: <path d="M20.8 5.6a5.5 5.5 0 0 0-7.8 0L12 6.6l-1-1a5.5 5.5 0 1 0-7.8 7.8l1 1L12 22l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z"/>,
    star: <path d="M12 3l2.6 5.3 5.9.9-4.2 4.1 1 5.8L12 16.9 6.7 19l1-5.8L3.5 9.2l5.9-.9z"/>,
    headphones: <><path d="M3 14v-2a9 9 0 0 1 18 0v2"/><path d="M21 16a2 2 0 0 1-2 2h-1v-6h1a2 2 0 0 1 2 2zM3 16a2 2 0 0 0 2 2h1v-6H5a2 2 0 0 0-2 2z"/></>,
    smile: <><circle cx="12" cy="12" r="9"/><path d="M8 14s1.5 2 4 2 4-2 4-2"/><line x1="9" y1="9" x2="9.01" y2="9"/><line x1="15" y1="9" x2="15.01" y2="9"/></>,
    wand: <><path d="M15 4V2M15 16v-2M8 9h2M20 9h2M17.8 11.8l1.4 1.4M17.8 6.2l1.4-1.4"/><path d="M3 21l9-9"/><path d="M12.5 6.5l1 1"/></>,
    shield: <><path d="M12 3l7 3v5c0 4.5-3 8-7 10-4-2-7-5.5-7-10V6z"/><path d="M9.5 12l1.8 1.8 3.7-3.7"/></>,
    lock: <><rect x="4.5" y="10.5" width="15" height="10" rx="2"/><path d="M8 10.5V7a4 4 0 0 1 8 0v3.5"/><circle cx="12" cy="15.5" r="1"/></>,
};

function Icon({name, className = '', strokeWidth = 1.9}) {
    return (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={strokeWidth}
             strokeLinecap="round" strokeLinejoin="round" className={className}>
            {ICONS[name] || null}
        </svg>
    );
}

// Чередующиеся оттенки палитры «Bedtime» для блоков с иконками.
const FEATURE_TINTS = [
    {bg: '#ECEAFF', fg: COLORS.primary},   // индиго
    {bg: '#EFEBFF', fg: COLORS.secondary}, // лаванда
    {bg: '#E6F4FF', fg: COLORS.info},      // голубой
    {bg: '#FFF6DD', fg: '#E0A636'},        // тёплый (звёзды)
];
const QUOTE_TINTS = [COLORS.primary, COLORS.secondary, COLORS.info, '#E0A636'];
// Светлый конец градиента для аватаров/кружков.
const TINT_FADE = COLORS.accent;

function PrimaryCta({label, onClick, className = ''}) {
    return (
        <button
            onClick={onClick}
            className={`inline-flex items-center justify-center gap-2 rounded-2xl px-7 h-14 bg-gradient-brand text-white text-[17px] font-bold tracking-[-0.01em] shadow-[0_10px_24px_rgba(90,79,243,0.32)] hover:-translate-y-0.5 hover:shadow-[0_14px_30px_rgba(90,79,243,0.42)] active:translate-y-0 transition ${className}`}
        >
            {label}
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"
                 strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h13M13 6l6 6-6 6"/>
            </svg>
        </button>
    );
}

// `onCtaClick` — переход в приложение (скролл к форме создания сказки).
// `heroVariant` — активный заголовок героя из HERO_VARIANTS (A/B-тест).
export default function Landing({onCtaClick, heroVariant}) {
    const {hero, features, appSection, testimonials, howItWorks, trust} = LANDING;
    const title = heroVariant?.title ?? '';
    const titleHighlight = heroVariant?.titleHighlight ?? '';

    // Скролл до встроенного приложения — тот же старт сценария, что и клик
    // по CTA, поэтому шлём ту же цель skazki_start (один раз за загрузку).
    const appSectionRef = useRef(null);
    useEffect(() => {
        const el = appSectionRef.current;
        if (!el || typeof IntersectionObserver === 'undefined') return undefined;
        let seen = false;
        const observer = new IntersectionObserver(entries => {
            if (seen || !entries.some(e => e.isIntersecting)) return;
            seen = true;
            trackEvent('skazki_start');
            observer.disconnect();
        }, {threshold: 0.15});
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    return (
        <div className="w-full">
            {/* ===== Hero ===== */}
            <section className="relative w-full overflow-hidden bg-brand-dark">
                {/* Фото показывается целиком, без обрезки: на мобильных — сверху, на десктопе — фоном под текстом. */}
                <img
                    src="/hero.png"
                    alt="Мама и ребёнок вместе читают сказку перед сном"
                    width="2848"
                    height="1502"
                    className="block w-full h-auto select-none"
                />
                {/* Горизонтальный скрим — затемняет левую часть под текст (десктоп). */}
                <div
                    className="hidden md:block absolute inset-0"
                    style={{background: 'linear-gradient(90deg, rgba(27,34,84,0.9) 0%, rgba(27,34,84,0.64) 34%, rgba(27,34,84,0.24) 58%, rgba(27,34,84,0) 78%)'}}
                />
                {/* Верхний скрим — под прозрачную шапку, чтобы её текст читался поверх фото. */}
                <div
                    className="absolute top-0 inset-x-0 h-28 md:h-32 pointer-events-none"
                    style={{background: 'linear-gradient(180deg, rgba(27,34,84,0.55) 0%, rgba(27,34,84,0) 100%)'}}
                />
                {/* Контент: на десктопе — поверх фото слева; на мобильных — под фото на тёмном фоне. */}
                <div className="md:absolute md:inset-0">
                  <div className="relative max-w-[1110px] mx-auto px-4 py-10 md:py-24 h-full flex items-center">
                    <div className="max-w-[560px]">
                        <span className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 px-4 py-1.5 text-[13px] md:text-sm font-semibold text-white">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor" className="text-star">
                                <path d="M12 3l2.6 5.3 5.9.9-4.2 4.1 1 5.8L12 16.9 6.7 19l1-5.8L3.5 9.2l5.9-.9z"/>
                            </svg>
                            {hero.badge}
                        </span>

                        <h1 className="mt-5 text-white font-bold leading-[1.08] tracking-[-0.02em] text-[34px] md:text-[52px]">
                            {title}{' '}
                            <span className="text-gradient">{titleHighlight}</span>
                        </h1>

                        <p className="mt-5 text-[16px] md:text-[18px] leading-[1.5] text-white/85 max-w-[480px]">
                            {hero.subtitle}
                        </p>

                        <div className="mt-8">
                            <PrimaryCta label={hero.cta} onClick={onCtaClick}/>
                        </div>

                        <div className="mt-7 flex items-center gap-3">
                            <div className="flex -space-x-2.5">
                                {testimonials.items.filter((t) => t.avatar).map((t, i) => (
                                    <img key={i} src={t.avatar} alt={t.name} loading="lazy" width="36" height="36"
                                         className="w-9 h-9 rounded-full object-cover border-2 border-white/80"/>
                                ))}
                            </div>
                            <span className="text-[14px] md:text-[15px] font-medium text-white/85">{hero.socialProof}</span>
                        </div>
                    </div>
                  </div>
                </div>
            </section>

            {/* ===== Полоса преимуществ ===== */}
            <section className="max-w-[1110px] mx-auto px-4 -mt-8 md:-mt-20 relative z-10">
                <div className="bg-white rounded-[24px] border border-black/5 shadow-[0_18px_50px_rgba(30,35,81,0.1)] px-5 py-7 md:px-9 md:py-9">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-7 md:gap-6">
                        {features.map((f, i) => {
                            const t = FEATURE_TINTS[i % FEATURE_TINTS.length];
                            return (
                                <div key={i} className="flex flex-col items-start">
                                    <span className="w-12 h-12 rounded-2xl flex items-center justify-center mb-3.5"
                                          style={{background: t.bg, color: t.fg}}>
                                        <Icon name={f.icon} className="w-6 h-6"/>
                                    </span>
                                    <h3 className="text-[16px] font-bold text-brand-dark leading-tight mb-1.5">{f.title}</h3>
                                    <p className="text-[14px] leading-[1.45] text-muted">{f.text}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ===== Приложение (второй экран) ===== */}
            <section ref={appSectionRef} className="pt-16 md:pt-24">
                <h2 className="text-center text-[26px] md:text-[34px] font-bold text-brand-dark tracking-[-0.02em] mb-6 md:mb-8 px-4">
                    {appSection.title}
                </h2>
                <div className="skazki-page py-2 md:py-4">
                    <SkazkiCard/>
                </div>
            </section>

            {/* ===== Отзывы ===== */}
            <section className="max-w-[1110px] mx-auto px-4 pt-16 md:pt-24">
                <h2 className="text-center text-[26px] md:text-[34px] font-bold text-brand-dark tracking-[-0.02em] mb-9 md:mb-12">
                    {testimonials.title} <span className="text-secondary">♥</span>
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    {testimonials.items.map((t, i) => {
                        const c = QUOTE_TINTS[i % QUOTE_TINTS.length];
                        return (
                            <div key={i} className="bg-white rounded-[20px] border border-black/5 shadow-[0_10px_30px_rgba(30,35,81,0.08)] p-6 flex flex-col">
                                <span className="text-[40px] leading-none font-serif font-bold mb-2" style={{color: c}}>“</span>
                                <p className="text-[15px] leading-[1.5] text-brand-dark font-medium flex-1">{t.quote}</p>
                                <div className="flex items-center gap-3 mt-5">
                                    {t.avatar ? (
                                        <img src={t.avatar} alt={t.name} loading="lazy" width="40" height="40"
                                             className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-[0_2px_8px_rgba(30,35,81,0.18)]"/>
                                    ) : (
                                        <span className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-[15px]"
                                              style={{background: `linear-gradient(135deg, ${c}, ${TINT_FADE})`}}>
                                            {t.name.slice(0, 1)}
                                        </span>
                                    )}
                                    <div>
                                        <div className="text-[15px] font-bold text-brand-dark leading-tight">{t.name}</div>
                                        <div className="text-[13px] text-muted">{t.sub}</div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* ===== Как это работает ===== */}
            <section className="max-w-[1110px] mx-auto px-4 pt-16 md:pt-24">
                <h2 className="text-center text-[26px] md:text-[34px] font-bold text-brand-dark tracking-[-0.02em] mb-10 md:mb-14">
                    <span className="text-star">✦</span> {howItWorks.title} <span className="text-star">✦</span>
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-7">
                    {howItWorks.steps.map((s, i) => (
                        <div key={i} className="flex flex-col items-center text-center md:items-start md:text-left">
                            <div className="flex items-center gap-4 mb-4">
                                <span className="w-11 h-11 rounded-full bg-gradient-brand text-white font-bold text-[18px] flex items-center justify-center shadow-[0_6px_16px_rgba(90,79,243,0.3)]">
                                    {i + 1}
                                </span>
                                <span className="w-14 h-14 rounded-2xl bg-brand-gray text-brand-red flex items-center justify-center">
                                    <Icon name={s.icon} className="w-7 h-7"/>
                                </span>
                            </div>
                            <h3 className="text-[19px] font-bold text-brand-dark leading-tight mb-2 max-w-[260px]">{s.title}</h3>
                            <p className="text-[15px] leading-[1.5] text-muted max-w-[260px]">{s.text}</p>
                        </div>
                    ))}
                </div>
                {/* CTA после блока «как это работает» */}
                <div className="flex justify-center mt-12 md:mt-14">
                    <PrimaryCta label={howItWorks.cta} onClick={onCtaClick} className="px-9 h-[60px]"/>
                </div>
            </section>

            {/* ===== Доверие ===== */}
            <section className="max-w-[1110px] mx-auto px-4 pt-16 md:pt-24 pb-6">
                <div className="bg-brand-gray rounded-[24px] px-5 py-9 md:px-10 md:py-10">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
                        {trust.map((t, i) => (
                            <div key={i} className="flex items-start gap-4">
                                <span className="shrink-0 w-12 h-12 rounded-2xl bg-white text-brand-red flex items-center justify-center shadow-[0_4px_12px_rgba(30,35,81,0.08)]">
                                    <Icon name={t.icon} className="w-6 h-6"/>
                                </span>
                                <div>
                                    <h3 className="text-[16px] font-bold text-brand-dark leading-tight mb-1.5">{t.title}</h3>
                                    <p className="text-[14px] leading-[1.45] text-muted">{t.text}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
